import { Button, PageHeader, Tabs } from "antd"
import PhoneVerificationModal from "Components/modals/phoneVerfication.modal"
import TfaModal from "Components/modals/tfa.modal"
import ToolSettingsModal from "Components/modals/toolSetting.modal"
import React, { useState } from "react"
import { Edit as EditIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import { useModal } from "Shared/context/modal.context"
import { useLoggedInEmployeeProfile } from "Shared/hooks/user.hooks"
import { disableTwoFactorAuth, setupSMSTwoFactorAuth } from "Shared/store/actions/auth/authActions"
import TroubleShootingSettings from "../troubleShootingSettings"

const { TabPane } = Tabs

const SecuritySettings = React.lazy(() => import("./securitySettings"))
const ToolsSettings = React.lazy(() => import("./toolSettings"))

enum ToolsAndSecurityActiveTabEnum {
    SECURITY,
    TOOL_SETTINGS,
    TROUBLESHOOTING,
}

const ToolsAndSecurityContainer = () => {
    const { t } = useTranslation()
    const { showModal } = useModal()
    const dispatch = useDispatch()
    const history = useHistory()

    const [activeKey, setActiveKey] = useState<Number>(ToolsAndSecurityActiveTabEnum.TOOL_SETTINGS)

    const loggedInUser = useLoggedInEmployeeProfile()

    const tabs = [
        {
            title: t("label:tool_settings"),
            key: ToolsAndSecurityActiveTabEnum.TOOL_SETTINGS,
            component: <ToolsSettings />,
        },
        {
            title: t("label:security"),
            key: ToolsAndSecurityActiveTabEnum.SECURITY,
            component: <SecuritySettings />,
        },
        {
            title: t("label:troubleshooting"),
            key: ToolsAndSecurityActiveTabEnum.TROUBLESHOOTING,
            component: <TroubleShootingSettings />,
        },
        // TODO enable this in next ticket
        /* { 
            title: 'Liquidity Management',
            component: <ToolsSettings />,
        }, */
    ]

    const setupSmsTfa = async () => {
        if (loggedInUser.getSecurityOptions()?.workPhoneVerified) {
            await dispatch(setupSMSTwoFactorAuth())
            return
        }

        showModal(PhoneVerificationModal, false, {
            isShowing: true,
            phoneNumber: loggedInUser.workPhone,
            employee: loggedInUser,
            onSuccess: setupSmsTfa,
        })
    }

    const disableTfa = async (checked: boolean) => await dispatch(disableTwoFactorAuth())

    const handleOnEditEvent = () => {
        switch (activeKey) {
            case ToolsAndSecurityActiveTabEnum.SECURITY:
                showModal(TfaModal, false, { isShowing: true, disableTfa, setupSmsTfa })
                break
            case ToolsAndSecurityActiveTabEnum.TOOL_SETTINGS:
                showModal(ToolSettingsModal, false, { employee: loggedInUser, isShowing: true })
                break
            default:
                break
        }
    }

    return (
        <div className="w-full">
            <div className="md:hidden">
                <PageHeader
                    ghost={false}
                    onBack={() => history.push("/settings")}
                    title={<h2 className="mt-4 mb-0 text-xs sm:text-sm md:text-lg">{t("label:tool_settings_security")}</h2>}
                    extra={[
                        activeKey !== ToolsAndSecurityActiveTabEnum.TROUBLESHOOTING && (
                            <Button key="editActionBtn" onClick={handleOnEditEvent}>
                                <EditIcon />
                            </Button>
                        ),
                    ]}
                ></PageHeader>
            </div>
            <div className="justify-between items-center mb-10 hidden md:flex">
                <h2 className="mb-0 mt-10">{t("label:tool_settings_security")}</h2>
                <div className="ml-10">
                    {activeKey !== ToolsAndSecurityActiveTabEnum.TROUBLESHOOTING && (
                        <Button onClick={handleOnEditEvent}>
                            <EditIcon />
                            <span>{t("action:edit")}</span>
                        </Button>
                    )}
                </div>
            </div>

            <Tabs onChange={(key) => setActiveKey(Number(key))}>
                {tabs.map((tab) => (
                    <TabPane tab={tab.title} key={tab.key} className="pb-10">
                        {tab.component}
                    </TabPane>
                ))}
            </Tabs>
        </div>
    )
}

export default React.memo(ToolsAndSecurityContainer)
